import { del, get, post, put } from "@/api/admin/client"
import { portalDel, portalGet, portalPost, portalPut } from "@/api/consumer/client"
import { isConsumerJwtSurface } from "@/utils/consumerSurface"

function nGet<T>(url: string): Promise<T> {
  if (typeof window !== "undefined" && isConsumerJwtSurface()) return portalGet<T>(url)
  return get<T>(url)
}

async function nPut<T>(url: string, body?: unknown): Promise<T> {
  if (typeof window !== "undefined" && isConsumerJwtSurface()) return portalPut<T>(url, body)
  return put<T>(url, body as never)
}

async function nPost<T>(url: string, body?: unknown): Promise<T> {
  if (typeof window !== "undefined" && isConsumerJwtSurface()) return portalPost<T>(url, body)
  return post<T>(url, body as never)
}

async function nDelete<T>(url: string): Promise<T> {
  if (typeof window !== "undefined" && isConsumerJwtSurface()) return portalDel<T>(url)
  return del<T>(url)
}

/** Notifikasi in-app (backend: NotificationModel) */
export interface InAppNotification {
  id: string
  user_id: string
  type: string
  title: string
  message: string
  resource_type?: string
  resource_id?: string | null
  is_read: boolean
  created_at: string
  read_at?: string | null
}

export interface NotificationsListResponse {
  data: InAppNotification[]
  total: number
  page: number
  limit: number
}

export const listNotifications = async (
  page = 1,
  limit = 20,
): Promise<NotificationsListResponse> => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) })
  return nGet<NotificationsListResponse>(`/notifications?${params}`)
}

/** Inbox terfilter (mis. hanya belum dibaca) - endpoint `/notifications/inbox`. */
export const listNotificationsInbox = async (opts: {
  page?: number
  limit?: number
  type?: string
  /** true = sudah dibaca, false = belum dibaca */
  isRead?: boolean
}): Promise<NotificationsListResponse> => {
  const page = opts.page ?? 1
  const limit = opts.limit ?? 20
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  })
  if (opts.type) params.set("type", opts.type)
  if (typeof opts.isRead === "boolean") params.set("is_read", String(opts.isRead))
  return nGet<NotificationsListResponse>(`/notifications/inbox?${params}`)
}

export const getNotification = async (id: string): Promise<InAppNotification> => {
  const res = await nGet<{ data: InAppNotification }>(`/notifications/${id}`)
  return res.data
}

export const getUnreadNotificationCount = async (): Promise<number> => {
  const res = await nGet<{ unread_count: number }>("/notifications/unread-count")
  return res.unread_count ?? 0
}

export const markNotificationRead = async (id: string): Promise<void> => {
  await nPut<{ message: string }>(`/notifications/${id}/read`)
}

export const markAllNotificationsRead = async (): Promise<void> => {
  await nPut<{ message: string }>("/notifications/read-all")
}

export const deleteNotification = async (id: string): Promise<void> => {
  await nDelete<{ message: string }>(`/notifications/${id}`)
}

export const deleteNotificationsBulk = async (ids: string[]): Promise<void> => {
  await nPost<{ message: string }>("/notifications/bulk-delete", { ids })
}

export const deleteAllNotifications = async (): Promise<void> => {
  await nDelete<{ message: string }>("/notifications/delete-all")
}
